import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { useLanguage } from './LanguageContext';

interface AuthUser {
  userId: string;
  username: string;
  email?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthLoadingMessage: React.FC<{ type: 'authenticating' | 'redirecting' }> = ({ type }) => {
  const { t } = useLanguage();
  return <div style={{ padding: '20px', textAlign: 'center' }}>{t('auth', type)}</div>;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();

      if (session.tokens?.idToken) {
        const payload = session.tokens.idToken.payload;
        setUser({
          userId: payload.sub as string || '',
          username: payload['cognito:username'] as string || '',
          email: payload.email as string || '',
        });
      } else {
        await signInWithRedirect();
      }
    } catch (error) {
      console.error('Auth check error:', error);
      try {
        await signInWithRedirect();
      } catch (redirectError) {
        console.error('Redirect error:', redirectError);
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const refreshSession = async () => {
    await checkAuth();
  };

  if (loading) {
    return <AuthLoadingMessage type="authenticating" />;
  }

  if (!user) {
    return <AuthLoadingMessage type="redirecting" />;
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};
